/**
 * CRT Subscriptions Queue -- platform admin view across all tenants.
 *
 * Sprint C-CRT-01 + 2026-05-16 role-equivalence fix.
 *
 * Lists every CRT subscription request across the network for Aaim
 * Platform Operators. Supports status filtering and sorting on the
 * primary operational fields. The manual "Mark wire received" action
 * is hidden behind a feature flag because the canonical
 * POST /api/v1/crt/oracle/wire-received endpoint requires HMAC
 * signature signing on the server's behalf; a server-side admin proxy
 * that performs the signing is filed as
 * F-CRT-MANUAL-WIRE-PROXY (TODO.md).
 *
 * Authz: gated via case-insensitive ANY-of role check accepting
 * `aaim_super_admin`, `platform_admin`, or `e2e_test_admin` (the three
 * role names that share the platform-admin permission scope by design
 * per aaim-core seed-rbac.ts; see fixture memory). Compare logic per
 * .claude/rules/authz-case-insensitive.md.
 */

import { useMemo, useState } from 'react';
import { AlertCircle, ShieldAlert } from 'lucide-react';
import {
  Card,
  Heading,
  Badge,
  Button,
  Select,
  FormLabel,
  Skeleton,
  TableScroll,
} from '@aaimplatform/ui';
import { useQuery } from '@tanstack/react-query';
import type { components as CrtComponents } from '@aaimplatform/institutional-types/generated/cooperative-reserve-token';
import { cooperativeReserveTokenClient } from '../../api/typed-clients';
import { useAuthStore } from '../../stores/auth';
import { hasAnyRole, PLATFORM_ADMIN_EQUIVALENT_ROLES } from '../../utils/role-helpers';

type SubscriptionDto = CrtComponents['schemas']['SubscriptionDto'];
type SubscriptionStatus = CrtComponents['schemas']['SubscriptionStatus'];

const STATUS_VALUES: SubscriptionStatus[] = [
  'PENDING',
  'FUNDING_PENDING',
  'FUNDED',
  'MINT_PENDING',
  'COMPLETED',
  'CANCELLED',
  'VALIDATION_ERROR',
];

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  PENDING: 'Awaiting wire',
  FUNDING_PENDING: 'Wire observed',
  FUNDED: 'Funded',
  MINT_PENDING: 'Crediting position',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  VALIDATION_ERROR: 'Needs reconciliation',
};

const STATUS_VARIANT: Record<SubscriptionStatus, 'info' | 'warning' | 'success' | 'error' | 'neutral'> = {
  PENDING: 'info',
  FUNDING_PENDING: 'warning',
  FUNDED: 'success',
  MINT_PENDING: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
  VALIDATION_ERROR: 'error',
};

/**
 * Feature flag for the manual ops mark-wire-received action. Disabled
 * until F-CRT-MANUAL-WIRE-PROXY ships the server-side HMAC signer.
 */
const MANUAL_WIRE_MARK_ENABLED = false;

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function formatCurrency(value: number): string {
  return CURRENCY.format(value);
}

type SortKey = 'requestedAt' | 'amount' | 'status';
type SortDir = 'asc' | 'desc';

export function CrtSubscriptionsQueue() {
  const { user } = useAuthStore();
  const isAuthorized = hasAnyRole(user?.roles, PLATFORM_ADMIN_EQUIVALENT_ROLES);

  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | 'ALL'>(
    'ALL',
  );
  const [sortKey, setSortKey] = useState<SortKey>('requestedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { data, isLoading, error } = useQuery({
    queryKey: ['crt-subscriptions-queue', statusFilter],
    queryFn: async () => {
      const { data, error } = await cooperativeReserveTokenClient.GET(
        '/api/v1/crt/subscriptions',
        {
          params: {
            query:
              statusFilter === 'ALL' ? undefined : { status: statusFilter },
          },
        },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ??
            'Failed to load subscriptions',
        );
      }
      return (data?.subscriptions ?? []) as SubscriptionDto[];
    },
    enabled: isAuthorized,
    refetchInterval: 15000,
    staleTime: 0,
    retry: false,
  });

  const subscriptions = useMemo(() => {
    const list = data ?? [];
    const sorted = [...list].sort((a, b) => {
      const direction = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'requestedAt':
          return (
            direction *
            (new Date(a.requestedAt).getTime() -
              new Date(b.requestedAt).getTime())
          );
        case 'amount':
          return direction * (a.amount - b.amount);
        case 'status':
          return direction * a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });
    return sorted;
  }, [data, sortKey, sortDir]);

  function toggleSort(nextKey: SortKey) {
    if (sortKey === nextKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(nextKey);
      setSortDir('desc');
    }
  }

  if (!isAuthorized) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldAlert className="w-12 h-12 text-warning mb-3" />
        <Heading level={2} size="card" className="mb-2">
          Restricted access
        </Heading>
        <p className="text-sm font-sans text-tertiary">
          The CRT subscriptions queue requires platform-admin scope.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm font-sans text-tertiary">
            All subscription requests across FI tenants. Operations
            reconciles wire receipts here when the Custodian FI callback
            is delayed or missing.
          </p>
        </div>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <FormLabel htmlFor="queue-status">Status filter</FormLabel>
            <Select
              id="queue-status"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as SubscriptionStatus | 'ALL')
              }
              options={[
                { value: 'ALL', label: 'All statuses' },
                ...STATUS_VALUES.map((s) => ({
                  value: s,
                  label: STATUS_LABEL[s],
                })),
              ]}
            />
          </div>
          <div className="md:col-span-2 text-xs font-sans text-tertiary">
            Polls every 15s. Click column headers to sort. Manual wire
            reconciliation is currently disabled.
          </div>
        </div>
      </Card>

      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {error instanceof Error ? error.message : 'Failed to load'}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : subscriptions.length === 0 ? (
        <Card className="p-6 bg-canvas/50">
          <div className="text-sm font-sans text-tertiary">
            No subscriptions match the current filter.
          </div>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <TableScroll label="Subscription requests">
          <table className="w-full min-w-table-wide text-sm font-sans">
            <thead className="bg-canvas/50 border-b border-recessed">
              <tr>
                <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                  <button
                    type="button"
                    onClick={() => toggleSort('requestedAt')}
                    className="font-medium hover:text-primary"
                  >
                    Requested
                    {sortKey === 'requestedAt' &&
                      (sortDir === 'asc' ? ' ↑' : ' ↓')}
                  </button>
                </th>
                <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                  Tenant
                </th>
                <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                  Funding Reference
                </th>
                <th className="text-right px-4 py-2 text-xs text-tertiary font-medium">
                  <button
                    type="button"
                    onClick={() => toggleSort('amount')}
                    className="font-medium hover:text-primary"
                  >
                    Amount
                    {sortKey === 'amount' &&
                      (sortDir === 'asc' ? ' ↑' : ' ↓')}
                  </button>
                </th>
                <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                  <button
                    type="button"
                    onClick={() => toggleSort('status')}
                    className="font-medium hover:text-primary"
                  >
                    Status
                    {sortKey === 'status' &&
                      (sortDir === 'asc' ? ' ↑' : ' ↓')}
                  </button>
                </th>
                <th className="text-right px-4 py-2 text-xs text-tertiary font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => (
                <tr
                  key={sub.requestId}
                  className="border-b border-recessed last:border-b-0"
                >
                  <td className="px-4 py-3 text-tertiary">
                    {new Date(sub.requestedAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 font-mono tabular-nums text-primary">
                    {sub.tenantId.slice(0, 8)}...
                  </td>
                  <td className="px-4 py-3 font-mono tabular-nums text-primary">
                    {sub.fundingReference.value}
                  </td>
                  <td className="px-4 py-3 text-right text-primary">
                    {formatCurrency(sub.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={STATUS_VARIANT[sub.status]} size="sm">
                      {STATUS_LABEL[sub.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {MANUAL_WIRE_MARK_ENABLED &&
                    (sub.status === 'PENDING' ||
                      sub.status === 'FUNDING_PENDING') ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        // Disabled until F-CRT-MANUAL-WIRE-PROXY ships.
                        disabled
                      >
                        Mark wire received
                      </Button>
                    ) : (
                      <span className="text-xs text-tertiary">--</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        </Card>
      )}
    </div>
  );
}
